import { Clock, Image as ImageIcon, X } from 'lucide-react'
import { useEffect, useId, useState, useSyncExternalStore, type CSSProperties, type ReactNode } from 'react'

import { ActionInfo, Button, DiagnosticCopy, formatBytes, formatDuration } from '@krp/ui-kit'

import { isLive, runProgress, runStepStates, type RunProgress, type RunStepId } from '../model/runView'
import { searchRun, type RunStage, type RunState, type SearchRun, type ServerStage } from '../model/searchRun'
import type { LineBand, PhotoRecognition, RecognizedLine } from '../model/types'
import { ProcessingView } from './ProcessingView'
import { WORD_FONT_REM } from './wordFont'

const SCOPE_TEXT: Record<string, string> = { key: 'крупные строки', 'key+medium': 'крупные и средние', all: 'весь текст' }
const scopeText = (scope: string): string => scope.split(',').map((part) => SCOPE_TEXT[part] ?? part).join(' = ')

const BAND_TEXT: Record<LineBand, string> = { key: 'крупное', medium: 'среднее', minor: 'мелкое', ignored: 'отброшено' }


/**
 * Слова с фото ОДНОЙ связной строкой через запятую (переносится только по ширине, без рамок и прокрутки). Размер слова зависит от
 * полосы веса (`WORD_FONT_REM`), над словом мелко — вес в процентах, если он есть; отброшенное (низкая уверенность или вне
 * рамки) зачёркнуто и без процента. Вес назван и словом (для диктора), и числом — не только размером и цветом.
 */
export function RecognizedWords({ lines }: { lines: readonly RecognizedLine[] }) {
  if (lines.length === 0) return null
  return (
    <ul className="platform-search__word-list" aria-label="Слова с фото">
      {lines.map((line, index) => (
        <li key={`${index}:${line.text}`} className="platform-search__word" data-band={line.band} title={BAND_TEXT[line.band]}>
          <span className="platform-search__word-weight">
            <span className="platform-search__sr">{BAND_TEXT[line.band]}</span>
            {line.band === 'ignored' ? '' : ` ${Math.round(line.weight * 100)}%`}
          </span>
          <span className="platform-search__word-line">
            <span className="platform-search__word-text" style={{ fontSize: `${WORD_FONT_REM[line.band]}rem` }}>
              {line.text}
            </span>
            {index < lines.length - 1 && (
              <span className="platform-search__word-sep" aria-hidden="true">
                ,
              </span>
            )}
          </span>
        </li>
      ))}
    </ul>
  )
}

/**
 * «Текст на фото» (решение владельца 23.10.2026): мелкая подпись и под ней сам текст — компактно, мелким шрифтом, целиком, без
 * прокрутки и рамки: блок растёт по содержимому. Есть строки с весами — слова одной строкой через запятую, нет — просто текст;
 * штрихкоды с кадра — отдельной строкой под блоком. `pending` — чтение ещё идёт.
 */
export function ReadTextBlock({ recognized, pending = false }: { recognized: PhotoRecognition | null; pending?: boolean }) {
  const labelId = useId()
  const lines = recognized?.lines ?? []
  const barcodes = recognized?.barcodes ?? []
  return (
    <div className="platform-search__readtext" role="group" aria-label="Распознано на фото">
      <span id={labelId} className="platform-search__readtext-label">
        Текст на фото
      </span>
      <div className="platform-search__readtext-box" role="group" aria-labelledby={labelId}>
        {recognized === null ? (
          <p className="platform-search__readtext-text">{pending ? 'Читаем текст…' : 'Текст не прочитан'}</p>
        ) : lines.length > 0 ? (
          <RecognizedWords lines={lines} />
        ) : (
          <p className="platform-search__readtext-text">{recognized.text ?? 'не распознан'}</p>
        )}
      </div>
      {barcodes.length > 0 && (
        <p className="platform-search__readtext-codes">
          Штрихкод на фото: <span>{barcodes.join(', ')}</span>
        </p>
      )}
    </div>
  )
}

/**
 * Снимок (слева, 104 px) с «часами» поверх, как в прежнем клиенте: кадр затемнён и светлеет по кругу от 12 часов по часовой стрелке
 * по мере общего хода (`progress.percent` — оценка). В центре значок: часы или «не вышло»; цифр и секунд на кадре нет.
 */
function RunShot({ run, url, progress }: { run: SearchRun; url: string | null | undefined; progress: RunProgress }) {
  const failed = run.phase === 'failed' || run.phase === 'cancelled'
  return (
    <div className="platform-run__shot" data-phase={run.phase} style={{ '--photo-progress': `${(progress.percent ?? 100) / 100}turn` } as CSSProperties}>
      {url === null || url === undefined ? (
        <span className="platform-run__shot-empty" aria-hidden="true">
          <ImageIcon size={32} />
        </span>
      ) : (
        <img src={url} alt="Ваш снимок" />
      )}
      <span className="platform-run__veil" aria-hidden="true" />
      <span className="platform-run__ring">{failed ? <X size={20} aria-hidden="true" /> : <Clock size={18} aria-hidden="true" />}</span>
    </div>
  )
}

/**
 * Полоса хода и под ней фраза этапа. Процент есть ТОЛЬКО у отправки снимка (настоящие байты); пока сервер ищет, процента нет — полоса «бежит» (идёт работа),
 * а фраза говорит, что именно делается (см. `runProgress`). Секунд нет. Вышли за обычное время — подпись «Ещё немного…».
 */
function ProgressBar({ progress }: { progress: RunProgress }) {
  return (
    <div className="platform-run__progress">
      <div
        className="platform-run__bar"
        role="progressbar"
        aria-label="Ход поиска"
        aria-valuemin={0}
        aria-valuemax={100}
        {...(progress.percent === null ? { 'aria-valuetext': 'идёт поиск' } : { 'aria-valuenow': progress.percent, 'aria-valuetext': `${progress.percent}%` })}
      >
        <span
          className="platform-run__bar-fill"
          data-indeterminate={progress.percent === null ? 'yes' : undefined}
          style={progress.percent === null ? undefined : { width: `${progress.percent}%` }}
        />
      </div>
      <div className="platform-run__row">
        {/* Живая область - только слова: процент меняется каждые доли секунды, диктор его не зачитывает (у полосы есть aria-valuenow). */}
        <p className="platform-run__phrase" role="status" aria-live="polite">
          <strong>{progress.phrase}</strong>
          {progress.stalled && <span className="platform-run__more">Ещё немного…</span>}
        </p>
        {progress.percent !== null && <span className="platform-run__percent">{progress.percent}%</span>}
      </div>
    </div>
  )
}

function useTicker(active: boolean): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!active) return undefined
    const timer = setInterval(() => setNow(Date.now()), 200)
    return () => clearInterval(timer)
  }, [active])
  return now
}

const plural = (n: number, one: string, few: string, many: string): string => {
  const tail = n % 100
  const last = n % 10
  if (tail >= 11 && tail <= 14) return many
  if (last === 1) return one
  return last >= 2 && last <= 4 ? few : many
}

export interface PlainStep {
  id: RunStepId
  title: string
  ms: number | null
  state: RunState
  text: string
  /** Запросы текстового поиска: что искали и сколько нашли. */
  queries: readonly string[]
}

const sumMs = (values: readonly (number | null)[]): number | null => {
  const known = values.filter((value): value is number => value !== null)
  return known.length === 0 ? null : known.reduce((total, value) => total + value, 0)
}

/** Слово о шаге, который не дошёл до «готово»: без технических кодов, с причиной, если сервер её прислал. */
function notDoneText(state: RunState, note: string | null): string {
  switch (state) {
    case 'failed':
      return note === null ? 'не получилось' : `не получилось: ${note}`
    case 'skipped':
      return note ?? 'не потребовалось'
    default:
      return 'до этого шага дело не дошло'
  }
}

/**
 * Разбор законченного поиска обычными словами по шагам: загрузка снимка, чтение текста, поиск по тексту, поиск по виду,
 * объединение результатов. Время — настоящее (`elapsedMs` этапов), числа — настоящие счётчики сервера; ничего не придумано.
 */
function plainSteps(run: SearchRun): PlainStep[] {
  const states = runStepStates(run)
  const stage = (name: ServerStage): RunStage => run.stages[name]
  const n = (name: ServerStage, key: string): number => stage(name).counts[key] ?? 0
  const noteOf = (...names: ServerStage[]): string | null => names.map((name) => stage(name).note).find((note) => note !== null) ?? null

  const shot = `${run.source === 'camera' ? 'Снимок снят камерой' : 'Снимок выбран'}${run.fileName === null ? '' : ` «${run.fileName}»`}${run.fileBytes === null ? '' : `, ${formatBytes(run.fileBytes)}`}`
  const compress =
    run.compress.state === 'skipped' && run.compress.toBytes !== null
      ? `кадр не перекодируется, уходит как есть: ${formatBytes(run.compress.toBytes)}`
      : null
  const focusRead = (run.recognized?.lines ?? []).filter((line) => line.focus === true && line.band !== 'ignored').map((line) => line.text)
  const sent = run.upload.state === 'done' ? 'отправили на сервер' : run.upload.state === 'failed' ? 'отправить не получилось' : 'отправка не завершена'

  const textStage = stage('text')
  const barcodeFound = stage('barcode').state === 'done' ? n('barcode', 'products') : 0
  const textDone = states.text === 'done'
  const queries = n('text', 'queries')
  const textBits = [
    textStage.state === 'done' ? `${queries} ${plural(queries, 'запрос', 'запроса', 'запросов')}, кандидатов среди товаров: ${n('text', 'candidates')}` : null,
    barcodeFound > 0 ? `по штрихкоду нашли товаров: ${barcodeFound}` : null,
    stage('prepare').state === 'done' ? `ждали, пока служба текстового поиска проснётся после простоя: ${formatDuration(stage('prepare').elapsedMs ?? 0)}` : null,
  ].filter((bit): bit is string => bit !== null)

  const unresolved = n('catalog', 'unresolved')
  const judgeStage = stage('judge')
  const judged = n('judge', 'candidates')

  const steps: PlainStep[] = [
    {
      id: 'upload',
      title: 'Загрузка снимка',
      ms: sumMs([run.compress.elapsedMs, run.upload.elapsedMs]),
      state: states.upload,
      text: [shot, compress, sent].filter((part): part is string => part !== null).join('; '),
      queries: [],
    },
    {
      id: 'read',
      title: 'Чтение текста на фото',
      ms: stage('read').elapsedMs,
      state: states.read,
      text:
        states.read === 'done'
          ? `прочитали строк: ${n('read', 'lines')} (крупных ${n('read', 'key')}, средних ${n('read', 'medium')}, мелких ${n('read', 'minor')}, лишних отбросили ${n('read', 'ignored')}), штрихкодов: ${n('read', 'barcodes')}${focusRead.length > 0 ? `; прицельно прочитано: ${focusRead.join(', ')}` : ''}`
          : notDoneText(states.read, noteOf('read')),
      queries: [],
    },
    {
      id: 'text',
      title: 'Поиск по тексту',
      ms: sumMs([textStage.elapsedMs, stage('barcode').elapsedMs, stage('prepare').elapsedMs]),
      state: states.text,
      text: textDone ? (textBits.length > 0 ? textBits.join('; ') : 'искать по тексту было нечего') : notDoneText(states.text, noteOf('text', 'barcode')),
      queries: textDone ? textStage.queries.map((query) => `${scopeText(query.scope)}: «${query.query}» — нашли ${query.found}, ${formatDuration(query.elapsedMs)}`) : [],
    },
    {
      id: 'visual',
      title: 'Поиск по виду',
      ms: sumMs([stage('signature').elapsedMs, stage('visual').elapsedMs]),
      state: states.visual,
      text:
        states.visual === 'done'
          ? `сравнили снимок с каталогом: похожих кандидатов ${n('visual', 'candidates')}, из них опознано товаров ${n('visual', 'resolved')}`
          : notDoneText(states.visual, noteOf('signature', 'visual')),
      queries: [],
    },
    {
      id: 'result',
      title: 'Объединение результатов',
      ms: sumMs([stage('merge').elapsedMs, stage('catalog').elapsedMs]),
      state: states.result,
      text:
        stage('catalog').state === 'done'
          ? `собрали ответы поисков в один список: товаров в выдаче ${n('catalog', 'hits')}${unresolved > 0 ? `; позиций индекса без товара в каталоге (в выдачу не попали): ${unresolved}` : ''}`
          : states.result === 'done'
            ? 'собрали ответы поисков в один список'
            : notDoneText(states.result, noteOf('merge', 'catalog')),
      queries: [],
    },
  ]
  // «Проверка» (RP-FB77 П4) бывает только у спорного ответа: сервер не присылал этапа — шага нет.
  if (judgeStage.state !== 'pending') {
    steps.push({
      id: 'judge',
      title: 'Проверка похожих вариантов',
      ms: judgeStage.elapsedMs,
      state: states.judge,
      text:
        judgeStage.state === 'done'
          ? `сверили снимок с вариантами проверяющей моделью: вариантов ${judged} (с фото ${n('judge', 'withPhoto')}); тот же товар — ${n('judge', 'same')}, другой — ${n('judge', 'different')}, не уверена — ${n('judge', 'unsure')}${judgeStage.note === null ? '' : `; ${judgeStage.note}`}`
          : notDoneText(states.judge, judgeStage.note),
      queries: [],
    })
  }
  return steps
}

const STATE_PLAIN: Record<RunState, string> = { pending: 'не начато', running: 'идёт', done: 'готово', failed: 'ошибка', skipped: 'пропущено' }

/**
 * Развёрнутый блок «Посмотреть детали поиска» (только администратору): сначала «Как обработано фото» (RP-FB55: исходное, кадр для поиска, что видит
 * энкодер), затем шаги простыми словами, технический идентификатор — мелко, под «Для администратора». Отдельной ссылки на обработку фото
 * в строке итога нет (решение владельца 23.10.2026): это часть деталей.
 */
function PlainDetails({ run, id, shotUrl, extra }: { run: SearchRun; id: string; shotUrl: string | null; extra: ReactNode }) {
  const steps = plainSteps(run)
  const discarded = (run.recognized?.lines ?? []).filter((line) => line.band === 'ignored')
  return (
    <div id={id} className="platform-search__details" role="region" aria-label="Подробности поиска">
      {run.processing !== null && <ProcessingView processing={run.processing} originalUrl={shotUrl} contrast={run.visualContrast} />}
      <ol className="platform-search__plain" aria-label="Что происходило при поиске">
        {steps.map((step) => (
          <li key={step.id} className="platform-search__plain-step" data-step={step.id} data-state={step.state}>
            <span className="platform-search__plain-head">
              <strong>{step.title}</strong>
              <span className="platform-search__plain-time">{step.ms === null ? STATE_PLAIN[step.state] : formatDuration(step.ms)}</span>
            </span>
            <span className="platform-search__plain-text">{step.text}</span>
            {step.queries.length > 0 && (
              <ul className="platform-search__queries" aria-label="Запросы к текстовому поиску">
                {step.queries.map((query) => (
                  <li key={query}>{query}</li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ol>
      {discarded.length > 0 && (
        <div className="platform-search__discarded">
          <p className="platform-search__admin-title">Строки текста, которые не искали</p>
          <ul aria-label="Отброшенные строки текста">
            {discarded.map((line, index) => (
              <li key={`${line.text}-${index}`}>
                «{line.text}» — {line.ignoredReason ?? 'шум чтения'}
              </li>
            ))}
          </ul>
        </div>
      )}
      {extra}
      {run.correlationId !== null && (
        <div className="platform-search__admin">
          <p className="platform-search__admin-title">Для администратора</p>
          <p className="platform-search__trace">Запрос {run.correlationId}</p>
          <DiagnosticCopy text={`Запрос ${run.correlationId}`} label="Копировать" />
        </div>
      )}
    </div>
  )
}

/** Заголовок законченного поиска: «Найдено за 5,2 с» (успех) или честное слово об обрыве. */
function finishedTitle(phase: SearchRun['phase'], total: number | null): string {
  const time = total === null ? null : formatDuration(total)
  if (phase === 'done') return time === null ? 'Поиск завершён' : `Найдено за ${time}`
  const word = phase === 'cancelled' ? 'Остановлено' : 'Не удалось'
  return time === null ? word : `${word} · ${time}`
}


/** Итог законченного поиска: «Найдено за 5,2 с» (успех) или честное слово об обрыве. Время — только здесь, итогом. */
function DoneSummary({ run, total, canSeeDetails, shotUrl, extra }: { run: SearchRun; total: number | null; canSeeDetails: boolean; shotUrl: string | null; extra: ReactNode }) {
  const [open, setOpen] = useState(false)
  const detailId = useId()
  return (
    <section className="platform-search__progress" data-phase={run.phase} aria-label="Итог поиска">
      <div className="platform-search__done-head">
        <strong className="platform-search__done-title">{finishedTitle(run.phase, total)}</strong>
        {/* Ссылка и сам блок (с обработкой фото внутри) — только системному администратору; у остальных их нет в разметке вовсе. */}
        {canSeeDetails && (
          <ActionInfo id="platform.search.progress-details" label="Посмотреть детали поиска">
            <button
              type="button"
              className="platform-search__link"
              aria-expanded={open}
              aria-controls={open ? detailId : undefined}
              onClick={() => setOpen((value) => !value)}
            >
              Посмотреть детали поиска
            </button>
          </ActionInfo>
        )}
      </div>
      {canSeeDetails && open && <PlainDetails run={run} id={detailId} shotUrl={shotUrl} extra={extra} />}
    </section>
  )
}

/**
 * Одна строка итога под снимком и над списком: «Найдено за 5,2 с» и (только администратору системы) ссылка «Посмотреть детали
 * поиска». Пока поиск идёт и пока его не было — ничего.
 */
export function SearchDoneLine({
  canSeeDetails = false,
  shotUrl = null,
  detailsExtra = null,
}: {
  canSeeDetails?: boolean
  shotUrl?: string | null | undefined
  /** Дополнительные сведения внутри раскрытых деталей (время по этапам): данные приносит приложение, поэтому узел собирает оно. */
  detailsExtra?: ReactNode
}) {
  const run = useSyncExternalStore(searchRun.subscribe, searchRun.get)
  if (run.phase === 'idle' || isLive(run.phase)) return null
  const total = run.startedAt === null || run.finishedAt === null ? null : run.finishedAt - run.startedAt
  return <DoneSummary run={run} total={total} canSeeDetails={canSeeDetails} shotUrl={shotUrl ?? null} extra={detailsExtra} />
}

/**
 * Ход ИДУЩЕГО поиска: снимок слева (с «часами» по общему проценту), справа блок «Текст на фото», под ними одна полоса хода
 * 0–100 % с фразой этапа и «Отменить». Секунд и миллисекунд в ходе нет — время показывается только итогом (`SearchDoneLine`).
 * Законченный поиск панель не рисует. Над сканером панель — отдельный лист поверх видоискателя: нижняя панель с затвором не двигается.
 */
export function SearchProgressPanel({ onCancel, shotUrl }: { onCancel?: (() => void) | undefined; shotUrl?: string | null | undefined }) {
  const run = useSyncExternalStore(searchRun.subscribe, searchRun.get)
  const running = isLive(run.phase)
  // Часы нужны только чтобы полоса плавно подрастала внутри этапа; на экран они не выводятся.
  const now = useTicker(running)
  if (!running) return null
  const progress = runProgress(run, now)
  const readState = run.stages.read.state
  return (
    <section className="platform-run" data-phase={run.phase} aria-label="Ход поиска" aria-busy="true">
      <div className="platform-run__top">
        <RunShot run={run} url={shotUrl} progress={progress} />
        <div className="platform-run__side">
          <ReadTextBlock recognized={run.recognized} pending={readState === 'pending' || readState === 'running'} />
        </div>
      </div>
      <ProgressBar progress={progress} />
      {onCancel !== undefined && (
        <div className="platform-run__actions">
          <Button variant="secondary" icon={<X size={18} aria-hidden="true" />} onClick={onCancel} actionId="platform.search.cancel">
            Отменить
          </Button>
        </div>
      )}
    </section>
  )
}
